import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ArrowBack from '@mui/icons-material/ArrowBack';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  createOnlineItem,
  listItems,
  listOnlineItems,
} from '../../../endpoints/chapterSupplies';
import type { CreateOnlineItemRequest } from '../../../types/chapterSupplies';

const initialForm: CreateOnlineItemRequest = {
  item_id: '',
  price: 0,
  is_featured: 0,
  is_discontinued: 0,
  short_description: null,
  image_url: null,
  sort_order: 0,
  category_ids: [],
  sub_category_ids: [],
  group_type_ids: [],
};

export default function OnlineItemCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateOnlineItemRequest>(initialForm);
  const [error, setError] = useState<string | null>(null);

  const { data: itemsData } = useApiGet(
    ['chapter-supplies-items', 500, 0],
    () => listItems({ limit: 500, offset: 0 }),
    { enabled: true, retry: 1 }
  );
  const { data: onlineItemsData } = useApiGet(
    ['chapter-supplies-online-items', 500, 0],
    () => listOnlineItems({ limit: 500, offset: 0 }),
    { enabled: true, retry: 1 }
  );

  const availableItems = useMemo(() => {
    const items = itemsData?.items ?? [];
    const onlineItemIds = new Set(
      (onlineItemsData?.online_items ?? []).map((o) => o.item_id)
    );
    return items.filter((i) => !onlineItemIds.has(i.id));
  }, [itemsData?.items, onlineItemsData?.online_items]);

  const { mutate: submitOnlineItem, isPending } = useApiSend(
    (data: CreateOnlineItemRequest) => createOnlineItem(data),
    (response) => {
      navigate(`/chapter-supplies-management/online-items/${response.id}`);
    },
    (err) => {
      setError(err?.message ?? 'Failed to create online item');
    },
    [['chapter-supplies-online-items']]
  );

  const handleChange = (
    field: keyof CreateOnlineItemRequest,
    value: string | number | null | undefined | string[]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value ?? null }));
    setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.item_id?.trim()) {
      setError('Item is required');
      return;
    }
    if (form.price < 0) {
      setError('Price must be 0 or greater');
      return;
    }
    const payload: CreateOnlineItemRequest = {
      item_id: form.item_id.trim(),
      price: Number(form.price),
      is_featured: form.is_featured ?? 0,
      is_discontinued: form.is_discontinued ?? 0,
      short_description: form.short_description?.trim() || null,
      image_url: form.image_url?.trim() || null,
      sort_order: form.sort_order ?? 0,
      category_ids: form.category_ids ?? [],
      sub_category_ids: form.sub_category_ids ?? [],
      group_type_ids: form.group_type_ids ?? [],
    };
    submitOnlineItem(payload);
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title="Create online item"
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() =>
                navigate('/chapter-supplies-management/online-items')
              }
            >
              Back to list
            </Button>
          }
          rightDom={undefined}
          subtitle={undefined}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="item-id-label">Item</InputLabel>
                <Select
                  labelId="item-id-label"
                  label="Item"
                  value={form.item_id ?? ''}
                  onChange={(e) =>
                    handleChange('item_id', e.target.value || undefined)
                  }
                >
                  <MenuItem value="">
                    <em>Select an item (not yet online)</em>
                  </MenuItem>
                  {availableItems.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                      {item.sku ? ` (${item.sku})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {availableItems.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  All items already have an online listing, or no items exist
                  yet.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                label="Price"
                value={form.price === 0 ? '' : form.price}
                onChange={(e) =>
                  handleChange(
                    'price',
                    e.target.value === '' ? 0 : Number(e.target.value)
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_featured === 1}
                    onChange={(e) =>
                      handleChange('is_featured', e.target.checked ? 1 : 0)
                    }
                  />
                }
                label="Featured"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_discontinued === 1}
                    onChange={(e) =>
                      handleChange('is_discontinued', e.target.checked ? 1 : 0)
                    }
                  />
                }
                label="Discontinued"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Short description"
                value={form.short_description ?? ''}
                onChange={(e) =>
                  handleChange('short_description', e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Image URL"
                value={form.image_url ?? ''}
                onChange={(e) => handleChange('image_url', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 1 }}
                label="Sort order"
                value={form.sort_order ?? ''}
                onChange={(e) =>
                  handleChange(
                    'sort_order',
                    e.target.value === '' ? 0 : Number(e.target.value)
                  )
                }
              />
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={isPending || !form.item_id?.trim() || form.price < 0}
              >
                {isPending ? 'Creating…' : 'Create online item'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
}
